import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, Calendar } from 'lucide-react';
import { Team } from '@/types/team';

interface TeamCardProps {
  team: Team;
  hoveredTeamId: string | null;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

export const TeamCard = ({
  team,
  hoveredTeamId,
  onMouseEnter,
  onMouseLeave
}: TeamCardProps) => {
  const getStatusBadgeColor = (status: 'active' | 'upcoming' | 'inactive' | undefined) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'upcoming':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'inactive':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString();
  };

  const isHovered = hoveredTeamId === team.id;

  return (
    <Card 
      className="relative overflow-hidden h-48 hover:shadow-lg transition-shadow duration-200"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-semibold flex items-start justify-between">
          <span className="truncate pr-2">{team.name}</span>
          {team.seasons && (
            <Badge className={`text-xs shrink-0 ${getStatusBadgeColor(team.seasons.status)}`}>
              {team.seasons.status || 'inactive'}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-2">
        {/* Season Info */}
        {team.seasons ? (
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span>{team.seasons.season_year} - {team.seasons.name}</span>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">No season assigned</div>
        )}

        {/* Division Info */}
        {team.divisions ? (
          <div className="text-sm">
            <span className="text-muted-foreground">Division: </span>
            <Badge variant="outline" className="text-xs">
              {team.divisions.name}
            </Badge>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">No division assigned</div>
        )}

        {/* Player Count */}
        <div className="flex items-center gap-2 text-sm">
          <Users className="h-4 w-4 text-muted-foreground" />
          <span>{team.player_count || 0} players</span>
        </div>

        {/* Birth Date Cutoff */}
        {team.birth_date_cutoff && (
          <div className="text-sm">
            <span className="text-muted-foreground">Birth cutoff: </span>
            <span>{formatDate(team.birth_date_cutoff)}</span>
          </div>
        )}

        {/* Created Date */}
        <div className="text-xs text-muted-foreground">
          Created: {new Date(team.created_at).toLocaleDateString()}
        </div>
      </CardContent>
      
      {/* Manage Team Button - Show on hover */}
      <div className={`absolute bottom-3 right-3 transition-opacity duration-200 ${
        isHovered ? 'opacity-100 visible' : 'opacity-0 invisible'
      }`}>
        <Button size="sm" asChild>
          <Link to={`/superadmin/team/${team.id}`}>
            <span className="hidden sm:inline">Manage Team</span>
            <span className="sm:hidden">Manage</span>
          </Link>
        </Button>
      </div>
    </Card>
  );
};